import {
  AccountListItemSchema,
  AdminAccountDetailSchema,
  AdminApproveRevisionDto,
  AdminRejectRevisionDto,
  ConversionReport,
  ConversionReportSchema,
  CreateAdminDocument,
  DocumentListItemSchema,
  PageDataSchema,
  ProgramStatus,
  ProgramWithDetailSchema,
  VerifyAdminDocument
} from "@humanex/interfaces";
import {
  AccountApiClient,
  ApiClientConfig,
  ApiError,
  BaseApiClient,
  PartnerApiClient,
  ProfileApiClient,
  PublicApiClient
} from "@humanex/ui/libs";
import { z } from "zod";

import { AppCfg } from "@/configs/app";

import { firebaseService } from "./firebase";

export { ApiError } from "@humanex/ui/libs";

const defaultConfig: ApiClientConfig = {
  baseURL: AppCfg.apiBaseUrl,
  getToken: async () => {
    const curUser = firebaseService.currentUser;
    if (!curUser) {
      throw new ApiError("Authentication required but no valid user found", 401);
    }
    const token = await curUser.getIdToken();
    if (!token) {
      throw new ApiError("Authentication required but no valid token found", 401);
    }
    return token;
  }
};

// Admin-specific API client
class AdminApiClient extends BaseApiClient {
  constructor(config: ApiClientConfig) {
    super({ ...config, baseEndpoint: "api/v1/admin" });
  }

  // Activation check (not under /admin prefix)
  async getActivationStatus() {
    const client = new BaseApiClient({
      ...defaultConfig,
      baseEndpoint: "api/v1/account/activation"
    });
    return (client as any).get(
      "status",
      z.object({
        accountType: z.string().nullable().optional(),
        status: z.string().nullable().optional()
      })
    );
  }

  // Dashboard
  async getConversionReport(): Promise<ConversionReport> {
    return this.get("reports/conversion", ConversionReportSchema);
  }

  async getRevenueReport(params: { from: string; to: string }) {
    return this.get(
      `reports/revenue?from=${params.from}&to=${params.to}`,
      z.object({
        depositRevenue: z.number(),
        depositRevenueByDay: z.array(z.object({ date: z.string(), amount: z.number() })),
        activationRevenue: z.number(),
        activationRevenueByAccountType: z.record(z.string(), z.number())
      })
    );
  }

  async getAgentPerformance(params: { page?: number; limit?: number }) {
    const qs = new URLSearchParams();
    if (params.page) qs.set("page", String(params.page));
    if (params.limit) qs.set("limit", String(params.limit));
    return this.get(
      `reports/agent-performance?${qs.toString()}`,
      z.object({
        agents: z.array(
          z.object({
            agentId: z.string(),
            agentName: z.string().nullable(),
            agentEmail: z.string().nullable(),
            candidateCount: z.number(),
            applicationCount: z.number(),
            acceptedCount: z.number(),
            totalCommissions: z.number()
          })
        ),
        meta: z.object({
          page: z.number(),
          limit: z.number(),
          total: z.number()
        })
      })
    );
  }

  // Accounts
  async getAccounts(params: {
    type?: string;
    search?: string;
    status?: string;
    page?: number;
    limit?: number;
  }) {
    const qs = new URLSearchParams();
    if (params.type) qs.set("activationType", params.type);
    if (params.search) qs.set("search", params.search);
    if (params.status) qs.set("activationStatus", params.status);
    qs.set("page", String((params.page ?? 1) - 1));
    if (params.limit) qs.set("limit", String(params.limit));
    return this.get(`accounts?${qs.toString()}`, PageDataSchema(AccountListItemSchema));
  }

  async getAccount(accountId: string) {
    return this.get(`accounts/${accountId}`, AdminAccountDetailSchema);
  }

  async suspendAccount(id: string) {
    return this.patch(`accounts/${id}/suspend`, z.any());
  }

  async deleteAccount(email: string): Promise<void> {
    await this.delete("accounts", z.any(), { data: { email } });
  }

  async activateAccount(id: string) {
    return this.patch(`accounts/${id}/activate`, z.any());
  }

  async forceActivateAccount(email: string, type: string) {
    const client = new BaseApiClient({
      ...defaultConfig,
      baseEndpoint: "api/v1/account"
    });
    return (client as any).post(
      "activate",
      z.object({ activated: z.array(z.string()), failed: z.array(z.string()) }),
      { type, emails: [email] }
    );
  }

  // Commissions
  async getCommissions(params: { status?: string; page?: number; limit?: number }) {
    const qs = new URLSearchParams();
    if (params.status) qs.set("status", params.status);
    if (params.page) qs.set("page", String(params.page));
    if (params.limit) qs.set("limit", String(params.limit));
    return this.get(
      `commissions?${qs.toString()}`,
      z.object({
        data: z.array(
          z.object({
            id: z.string(),
            agentName: z.string().nullable().optional(),
            amount: z.number(),
            level: z.number().nullable().optional(),
            trigger: z.string().nullable().optional(),
            applicationId: z.string().nullable().optional(),
            status: z.string(),
            createdAt: z.string().nullable().optional()
          })
        ),
        total: z.number(),
        page: z.number(),
        limit: z.number()
      })
    );
  }

  async approveCommission(id: string) {
    return this.patch(`commissions/${id}/approve`, z.any());
  }

  async markCommissionPaid(id: string, paidNote: string) {
    return this.patch(`commissions/${id}/mark-paid`, z.any(), { paidNote });
  }

  // Documents
  async listDocuments(params: {
    refType?: string;
    refId?: string;
    type?: string;
    verified?: boolean;
    page?: number;
    limit?: number;
  }) {
    const qs = new URLSearchParams();
    if (params.refType) qs.set("refType", params.refType);
    if (params.refId) qs.set("refId", params.refId);
    if (params.type) qs.set("type", params.type);
    if (params.verified !== undefined) qs.set("verified", String(params.verified));
    qs.set("page", String((params.page ?? 1) - 1));
    if (params.limit) qs.set("limit", String(params.limit));
    return this.get(`documents?${qs.toString()}`, PageDataSchema(DocumentListItemSchema));
  }

  async createDocument(dto: CreateAdminDocument) {
    return this.post("documents", DocumentListItemSchema, dto);
  }

  async verifyDocument(id: string, dto: VerifyAdminDocument) {
    return this.patch(`documents/${id}/verify`, DocumentListItemSchema, dto);
  }

  async deleteDocument(id: string) {
    return this.delete(`documents/${id}`, z.any());
  }

  // Programs — review queue
  async listPendingPrograms(params: { search?: string; page?: number; limit?: number } = {}) {
    const qs = new URLSearchParams();
    qs.set("status", ProgramStatus.PENDING_REVIEW);
    qs.set("page", String(params.page ?? 0));
    qs.set("limit", String(params.limit ?? 20));
    if (params.search) qs.set("search", params.search);
    return this.get(`programs?${qs.toString()}`, PageDataSchema(ProgramWithDetailSchema));
  }

  async getProgramForReview(id: string) {
    return this.get(`programs/${id}`, ProgramWithDetailSchema);
  }

  async approveRevision(revisionId: string, dto: AdminApproveRevisionDto) {
    return this.post(`programs/revisions/${revisionId}/approve`, z.any(), dto);
  }

  async rejectRevision(revisionId: string, dto: AdminRejectRevisionDto) {
    return this.post(`programs/revisions/${revisionId}/reject`, z.any(), dto);
  }
}

export const adminApiClient = new AdminApiClient(defaultConfig);
export const accountApiClient = new AccountApiClient(defaultConfig);
export const profileApiClient = new ProfileApiClient(defaultConfig);
export const publicApiClient = new PublicApiClient(defaultConfig);
export const partnerApiClient = new PartnerApiClient(defaultConfig);
